import { Shield, CheckCircle, AlertTriangle, Zap, ArrowRight, Github, Menu, X } from 'lucide-react';
import { useState } from 'react';

const signals = [
  {
    icon: CheckCircle,
    name: 'Exact Match Rate',
    description: 'Compare outputs against expected strings. Pass/fail on exact match.',
    color: 'text-pass',
  },
  {
    icon: AlertTriangle,
    name: 'Length Ratio',
    description: 'Flag outputs that are significantly shorter or longer than expected.',
    color: 'text-warn',
  },
  {
    icon: Shield,
    name: 'Keyword Presence',
    description: 'Ensure required keywords are present and denied keywords are absent.',
    color: 'text-accent',
  },
  {
    icon: Zap,
    name: 'Token Distribution',
    description: 'Statistical outlier detection on output verbosity using z-scores.',
    color: 'text-info',
  },
];

const steps = [
  {
    number: '1',
    title: 'Define test cases in YAML',
    description: 'Commit input/expected output pairs to your repo alongside your code.',
    code: `test_cases:
  - name: "greeting response"
    input: "Hello, I need help"
    expected_output: "I'd be happy to help"
    signals: [exact_match, length_ratio]`,
  },
  {
    number: '2',
    title: 'Connect your CI pipeline',
    description: 'Add 3 lines to your GitHub Actions workflow.',
    code: `- name: Regression Guard
  uses: regressionguard/action@v1
  with:
    api_key: \${{ secrets.REGRESSION_GUARD_KEY }}
    pipeline_id: \${{ vars.REGRESSION_PIPELINE_ID }}
    test_output: \${{ steps.ai-output.outputs.result }}`,
  },
  {
    number: '3',
    title: 'Block bad deployments',
    description: 'Fail the build when regression is detected. Get detailed reports in PR comments.',
    code: `❌ Regression detected: 2 test(s) failed
✅ exact_match: PASSED
❌ length_ratio: FAILED (ratio: 1.45, threshold: 0.2)`,
  },
];

const plans = [
  {
    name: 'Starter',
    price: 79,
    description: 'For individual developers and small projects.',
    features: ['3 pipelines', '500 test runs/month', 'GitHub Actions integration', 'Basic regression reports', 'Email support'],
    cta: 'Start Free Trial',
    highlighted: false,
  },
  {
    name: 'Pro',
    price: 299,
    description: 'For growing teams with multiple AI pipelines.',
    features: ['10 pipelines', '5,000 test runs/month', 'GitLab CI + Slack alerts', 'Advanced regression reports', 'Threshold learning', 'Priority support'],
    cta: 'Start Free Trial',
    highlighted: true,
  },
  {
    name: 'Enterprise',
    price: 2499,
    description: 'For organizations with enterprise needs.',
    features: ['Unlimited pipelines', 'Unlimited test runs', 'Custom thresholds', 'SLA + dedicated support', 'SSO/SAML', 'Custom integrations'],
    cta: 'Contact Sales',
    highlighted: false,
  },
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAnnual, setShowAnnual] = useState(true);

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2">
              <Shield className="w-6 h-6 text-accent" />
              <span className="font-mono font-semibold text-text-primary">Regression Guard</span>
            </div>

            {/* Desktop nav */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#how-it-works" className="text-text-secondary hover:text-text-primary transition-colors text-sm">How It Works</a>
              <a href="#signals" className="text-text-secondary hover:text-text-primary transition-colors text-sm">Signals</a>
              <a href="#pricing" className="text-text-secondary hover:text-text-primary transition-colors text-sm">Pricing</a>
              <a href="/dashboard" className="text-text-secondary hover:text-text-primary transition-colors text-sm">Dashboard</a>
              <a href="/dashboard" className="px-4 py-2 bg-accent hover:bg-accent/90 text-white text-sm font-medium rounded-lg transition-colors">
                Get Started
              </a>
            </div>

            {/* Mobile menu button */}
            <button
              className="md:hidden p-2 text-text-secondary"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-surface border-t border-border px-4 py-4">
            <div className="flex flex-col gap-4">
              <a href="#how-it-works" className="text-text-secondary hover:text-text-primary">How It Works</a>
              <a href="#signals" className="text-text-secondary hover:text-text-primary">Signals</a>
              <a href="#pricing" className="text-text-secondary hover:text-text-primary">Pricing</a>
              <a href="/dashboard" className="text-text-secondary hover:text-text-primary">Dashboard</a>
              <a href="/dashboard" className="px-4 py-2 bg-accent text-white text-center font-medium rounded-lg">Get Started</a>
            </div>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent/10 border border-accent/20 rounded-full text-accent text-xs font-medium mb-6">
            <Github className="w-3 h-3" />
            GitHub Actions Integration
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-mono font-bold text-text-primary mb-6 leading-tight">
            Fail your build when<br />
            <span className="text-accent">AI quality drops.</span>
          </h1>

          <p className="text-lg text-text-secondary max-w-2xl mx-auto mb-8">
            CI/CD-native regression detection for AI pipelines. Catch when your AI agent outputs silently degrade — before they reach your users.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/dashboard" className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent hover:bg-accent/90 text-white font-medium rounded-lg transition-colors">
              Start Free Trial
              <ArrowRight className="w-4 h-4" />
            </a>
            <a href="#how-it-works" className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-border hover:border-text-secondary text-text-secondary hover:text-text-primary font-medium rounded-lg transition-colors">
              See How It Works
            </a>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-surface/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-mono font-bold text-text-primary text-center mb-4">How It Works</h2>
          <p className="text-text-secondary text-center mb-12 max-w-xl mx-auto">
            Three steps to add AI regression detection to your CI/CD pipeline.
          </p>

          <div className="grid md:grid-cols-3 gap-6">
            {steps.map((step) => (
              <div key={step.number} className="bg-surface border border-border rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-accent/10 border border-accent/20 rounded-lg flex items-center justify-center text-accent font-mono font-bold text-sm">
                    {step.number}
                  </div>
                  <h3 className="font-semibold text-text-primary">{step.title}</h3>
                </div>
                <p className="text-text-secondary text-sm mb-4">{step.description}</p>
                <pre className="text-xs text-text-secondary bg-background p-3 rounded-lg overflow-x-auto font-mono">
                  {step.code}
                </pre>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Regression Signals */}
      <section id="signals" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-mono font-bold text-text-primary text-center mb-4">Regression Signals</h2>
          <p className="text-text-secondary text-center mb-12 max-w-xl mx-auto">
            Four simple, reliable signals that catch AI quality degradation.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {signals.map((signal) => (
              <div key={signal.name} className="bg-surface border border-border rounded-xl p-6 card-hover">
                <signal.icon className={`w-8 h-8 ${signal.color} mb-4`} />
                <h3 className="font-semibold text-text-primary mb-2">{signal.name}</h3>
                <p className="text-text-secondary text-sm">{signal.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-20 px-4 sm:px-6 lg:px-8 bg-surface/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-mono font-bold text-text-primary text-center mb-4">Pricing</h2>
          <p className="text-text-secondary text-center mb-8">Start free. Scale as you grow.</p>

          {/* Annual/Monthly toggle */}
          <div className="flex justify-center mb-12">
            <div className="inline-flex items-center gap-3 p-1 bg-background rounded-lg border border-border">
              <button
                onClick={() => setShowAnnual(false)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${!showAnnual ? 'bg-accent text-white' : 'text-text-secondary hover:text-text-primary'}`}
              >
                Monthly
              </button>
              <button
                onClick={() => setShowAnnual(true)}
                className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${showAnnual ? 'bg-accent text-white' : 'text-text-secondary hover:text-text-primary'}`}
              >
                Annual
                <span className="ml-2 text-xs text-pass">Save 20%</span>
              </button>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`rounded-xl p-6 ${plan.highlighted ? 'bg-accent/5 border-2 border-accent' : 'bg-surface border border-border'}`}
              >
                {plan.highlighted && (
                  <div className="inline-block px-2 py-1 bg-accent/10 border border-accent/20 rounded text-accent text-xs font-medium mb-4">
                    Most Popular
                  </div>
                )}
                <h3 className="text-lg font-semibold text-text-primary mb-2">{plan.name}</h3>
                <div className="mb-4">
                  <span className="text-3xl font-mono font-bold text-text-primary">${showAnnual ? Math.round(plan.price * 0.8) : plan.price}</span>
                  <span className="text-text-secondary text-sm">/mo</span>
                </div>
                <p className="text-text-secondary text-sm mb-6">{plan.description}</p>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2 text-sm text-text-secondary">
                      <CheckCircle className="w-4 h-4 text-pass mt-0.5 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
                <button className={`w-full py-3 font-medium rounded-lg transition-colors ${plan.highlighted ? 'bg-accent hover:bg-accent/90 text-white' : 'border border-border hover:border-text-secondary text-text-primary'}`}>
                  {plan.cta}
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Beachhead CTA */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl font-mono font-bold text-text-primary mb-4">
            Built for customer support AI teams
          </h2>
          <p className="text-text-secondary mb-8">
            SaaS companies running LLM-based support agents face the highest stakes when AI quality degrades. Regression Guard catches issues before they impact CSAT scores.
          </p>
          <a href="/dashboard" className="inline-flex items-center gap-2 px-6 py-3 bg-accent hover:bg-accent/90 text-white font-medium rounded-lg transition-colors">
            Start Free Trial
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 sm:px-6 lg:px-8 border-t border-border">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-accent" />
              <span className="font-mono font-semibold text-text-primary">Regression Guard</span>
            </div>
            <div className="flex items-center gap-6 text-sm text-text-secondary">
              <a href="#" className="hover:text-text-primary transition-colors">Documentation</a>
              <a href="#" className="hover:text-text-primary transition-colors">GitHub</a>
              <a href="#" className="hover:text-text-primary transition-colors">Support</a>
            </div>
            <p className="text-sm text-text-secondary">© 2024 Regression Guard. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}